import React, { useState, useEffect } from 'react';
import { confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import { auth } from '../firebase';

// ── Icons ──────────────────────────────────────────────────────────────────

const PwIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round"
    style={{ width: 15, height: 15, color: '#bbb', flexShrink: 0 }}>
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
  </svg>
);

const EyeIcon = ({ visible }) => visible ? (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round"
    style={{ width: 16, height: 16 }}>
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
) : (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round"
    style={{ width: 16, height: 16 }}>
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
    <line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
);

const ErrorIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round"
    style={{ width: 15, height: 15, flexShrink: 0 }}>
    <circle cx="12" cy="12" r="10"/>
    <line x1="12" y1="8" x2="12" y2="12"/>
    <line x1="12" y1="16" x2="12.01" y2="16"/>
  </svg>
);

const CheckCircleIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round"
    style={{ width: 40, height: 40, stroke: 'var(--orange)' }}>
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
    <polyline points="22 4 12 14.01 9 11.01"/>
  </svg>
);

const RESET_ERRORS = {
  'auth/expired-action-code': 'This reset link has expired. Please request a new one from the sign-in page.',
  'auth/invalid-action-code': 'This link has already been used or is invalid. Please request a new one.',
  'auth/weak-password': 'Password must be at least 8 characters.',
  'auth/user-disabled': 'This account has been disabled. Please contact support@thetalentmapper.com.',
};

// ── PasswordField ──────────────────────────────────────────────────────────

function PasswordField({ label, value, onChange, show, onToggle, placeholder, autoComplete }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{
        display: 'block', fontSize: 11, fontWeight: 600,
        color: 'var(--muted)', letterSpacing: '.6px', textTransform: 'uppercase',
        marginBottom: 6,
      }}>
        {label}
      </label>
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        <span style={{ position: 'absolute', left: 12, display: 'flex', pointerEvents: 'none' }}>
          <PwIcon />
        </span>
        <input
          type={show ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          autoComplete={autoComplete}
          required
          style={{
            width: '100%', fontFamily: 'var(--sans)', fontSize: 14, color: 'var(--black)',
            padding: '10px 44px 10px 38px',
            border: '1.5px solid var(--line)', borderRadius: 3,
            background: 'var(--white)', outline: 'none',
            transition: 'border-color .15s',
          }}
          onFocus={(e) => { e.target.style.borderColor = 'var(--orange)'; }}
          onBlur={(e) => { e.target.style.borderColor = 'var(--line)'; }}
        />
        <button
          type="button"
          onClick={onToggle}
          style={{
            position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
            background: 'none', border: 'none', cursor: 'pointer', padding: 0,
            color: 'var(--muted)', display: 'flex', alignItems: 'center',
          }}
          aria-label={show ? 'Hide password' : 'Show password'}
        >
          <EyeIcon visible={show} />
        </button>
      </div>
    </div>
  );
}

// ── ResetPassword ──────────────────────────────────────────────────────────

/**
 * ResetPassword
 *
 * Rendered by App.js when the URL contains ?mode=resetPassword&oobCode=...
 * (i.e. the user clicked a password reset link from their email).
 *
 * Props:
 *   oobCode  — Firebase out-of-band code from the URL
 *   onDone   — called when the user clicks "Back to sign in" after success,
 *              or "Back to sign in" from the expired-link error state
 */
function ResetPassword({ oobCode, onDone }) {
  const [verifying, setVerifying] = useState(true);
  const [codeValid, setCodeValid] = useState(false);
  const [email, setEmail] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Verify the oobCode is still valid before showing the form
  useEffect(() => {
    verifyPasswordResetCode(auth, oobCode)
      .then((accountEmail) => {
        setEmail(accountEmail);
        setCodeValid(true);
        setVerifying(false);
      })
      .catch(() => {
        setError('This password reset link has expired or has already been used. Please request a new one from the sign-in page.');
        setVerifying(false);
      });
  }, [oobCode]);

  const canSubmit = !!(newPw && confirmPw && !loading && codeValid);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    if (newPw.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (newPw !== confirmPw) { setError('Passwords do not match.'); return; }

    setLoading(true);
    setError(null);
    try {
      await confirmPasswordReset(auth, oobCode, newPw);
      setSuccess(true);
    } catch (err) {
      setError(RESET_ERRORS[err.code] || 'Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      backgroundImage: 'url(/assets/Art.png)',
      backgroundSize: 'cover', backgroundPosition: 'center',
      padding: '20px',
    }}>
      <div style={{
        background: 'var(--white)', width: '100%', maxWidth: 420,
        borderRadius: 6, overflow: 'hidden',
        boxShadow: '0 8px 32px rgba(33,33,33,.18)',
        animation: 'fadeUp .35s var(--ease) both',
      }}>
        <div style={{ padding: '44px 48px 40px' }}>

          {/* Logo */}
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 28 }}>
            <img src="/assets/default-logo.svg" alt="TalentMapper" style={{ height: 26, width: 'auto', display: 'block' }} />
          </div>

          {/* Verifying */}
          {verifying && (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <img src="/assets/default-loading.gif" alt="Checking link…" style={{ width: 36, height: 36 }} />
            </div>
          )}

          {/* Success */}
          {!verifying && success && (
            <div style={{ textAlign: 'center' }}>
              <div style={{
                width: 64, height: 64, background: 'var(--almond)', borderRadius: '50%',
                margin: '0 auto 16px', display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <CheckCircleIcon />
              </div>
              <h2 style={{
                fontFamily: 'var(--serif)', fontSize: 20, fontWeight: 600,
                color: 'var(--black)', marginBottom: 8, marginTop: 0,
              }}>
                Password updated
              </h2>
              <p style={{ fontSize: 14, color: 'var(--mid)', lineHeight: 1.6, marginBottom: 24 }}>
                Your password has been set successfully. You can now sign in with your new password.
              </p>
              <button
                onClick={onDone}
                style={{
                  width: '100%', padding: 13,
                  fontFamily: 'var(--sans)', fontSize: 14, fontWeight: 600,
                  background: 'var(--orange)', color: 'var(--white)',
                  border: 'none', borderRadius: 3, cursor: 'pointer',
                  transition: 'background .15s',
                }}
                onMouseEnter={(e) => { e.target.style.background = 'var(--orange-dark)'; }}
                onMouseLeave={(e) => { e.target.style.background = 'var(--orange)'; }}
              >
                Back to sign in
              </button>
            </div>
          )}

          {/* Invalid / expired code */}
          {!verifying && !codeValid && !success && (
            <div>
              {error && (
                <div style={{
                  display: 'flex', alignItems: 'flex-start', gap: 8,
                  background: 'var(--err-bg)', borderRadius: 3,
                  padding: '10px 12px', fontSize: 13, color: 'var(--err-txt)',
                  marginBottom: 20,
                }}>
                  <ErrorIcon />
                  {error}
                </div>
              )}
              <button
                onClick={onDone}
                style={{
                  width: '100%', padding: 13,
                  fontFamily: 'var(--sans)', fontSize: 14, fontWeight: 600,
                  background: 'var(--orange)', color: 'var(--white)',
                  border: 'none', borderRadius: 3, cursor: 'pointer',
                  transition: 'background .15s',
                }}
                onMouseEnter={(e) => { e.target.style.background = 'var(--orange-dark)'; }}
                onMouseLeave={(e) => { e.target.style.background = 'var(--orange)'; }}
              >
                Back to sign in
              </button>
            </div>
          )}

          {/* Form */}
          {!verifying && codeValid && !success && (
            <div>
              <h2 style={{
                fontFamily: 'var(--serif)', fontSize: 20, fontWeight: 600,
                color: 'var(--black)', marginBottom: 4, marginTop: 0,
              }}>
                Set new password
              </h2>
              {email && (
                <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 20, lineHeight: 1.5 }}>
                  For <strong style={{ color: 'var(--black)', fontWeight: 600 }}>{email}</strong>
                </p>
              )}

              {error && (
                <div style={{
                  display: 'flex', alignItems: 'flex-start', gap: 8,
                  background: 'var(--err-bg)', borderRadius: 3,
                  padding: '10px 12px', fontSize: 13, color: 'var(--err-txt)',
                  marginBottom: 16,
                }}>
                  <ErrorIcon />
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} noValidate>
                <PasswordField
                  label="New password"
                  value={newPw}
                  onChange={setNewPw}
                  show={showNew}
                  onToggle={() => setShowNew((v) => !v)}
                  placeholder="Min. 8 characters"
                  autoComplete="new-password"
                />
                <div style={{ marginBottom: 24 }}>
                  <PasswordField
                    label="Confirm new password"
                    value={confirmPw}
                    onChange={setConfirmPw}
                    show={showConfirm}
                    onToggle={() => setShowConfirm((v) => !v)}
                    placeholder="Repeat new password"
                    autoComplete="new-password"
                  />
                </div>

                <button
                  type="submit"
                  disabled={!canSubmit}
                  style={{
                    width: '100%', padding: 13,
                    fontFamily: 'var(--sans)', fontSize: 14, fontWeight: 600,
                    background: canSubmit ? 'var(--orange)' : '#f0c4a0',
                    color: 'var(--white)', border: 'none', borderRadius: 3,
                    cursor: canSubmit ? 'pointer' : 'default',
                    letterSpacing: '.2px', transition: 'background .15s',
                  }}
                  onMouseEnter={(e) => { if (canSubmit) e.target.style.background = 'var(--orange-dark)'; }}
                  onMouseLeave={(e) => { if (canSubmit) e.target.style.background = 'var(--orange)'; }}
                >
                  {loading ? 'Updating…' : 'Set new password'}
                </button>
              </form>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

export default ResetPassword;
